import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { config } from '@services/context';
import { SearchGroupedProducts } from '@shared/components/SearchGroupedProducts';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useProductSelection } from '@shared/hooks/useProductSelection';
import { Product } from '@shared/protos/product';
import { useCallback, useMemo } from 'react';

type Props = {
  selectedColIds: string[];
  selectedUserProducts: Product[];
  addColumn: (id: string) => void;
  toggleColumn: (id: string) => void;
  maxProductsLimit?: number;
};

export const ProductsSelectionForm = ({ selectedColIds, selectedUserProducts, maxProductsLimit, addColumn, toggleColumn }: Props) => {
  const { userProducts, loaded } = useUserProductsAndTenorsContext();
  maxProductsLimit = maxProductsLimit || config.maxProductsLimit;

  const { allProducts, allSelectedProducts, getProductCount, isSelectionDisabled, onChangeValues } = useProductSelection({
    userProducts,
    selectedColIds,
    selectedUserProducts,
    toggleColumn,
    addColumn,
    maxProductsLimit,
  });

  const allProductGroups = useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    allProducts.forEach(product => {
      if (!groups[product.product_group]) {
        groups[product.product_group] = [];
      }
      groups[product.product_group].push(product);
    });
    return groups;
  }, [allProducts]);

  const onCheckboxChange = useCallback(
    (product: Product) => {
      const newSelectedValues = allSelectedProducts.find(elem => elem.symbol === product.symbol)
        ? allSelectedProducts.filter(({ symbol }) => symbol !== product.symbol)
        : [...allSelectedProducts, product];
      onChangeValues(newSelectedValues.map(({ symbol }) => allProducts.find(product => product.symbol === symbol)!));
    },
    [allSelectedProducts, onChangeValues, allProducts]
  );

  const onRemoveProduct = useCallback(
    (productSymbol: string) => {
      const newSelectedValues = allSelectedProducts.filter(({ symbol }) => symbol !== productSymbol);
      onChangeValues(newSelectedValues.map(({ symbol }) => allProducts.find(product => product.symbol === symbol)!));
    },
    [onChangeValues, allProducts]
  );

  return loaded && allProducts.length ? (
    <Stack direction="column" spacing={2}>
      {selectedUserProducts.length > maxProductsLimit && (
        <Typography textAlign="left" variant="body" sx={{ color: 'error.main' }}>
          You are over the limit of {maxProductsLimit} products. Please remove some products to continue.
        </Typography>
      )}

      <Box display="flex" flex={1}>
        <Box display="flex" flexWrap="wrap" flex={1} gap={1}>
          {allSelectedProducts.map(({ symbol, full_name }) => (
            <Chip key={`${symbol}-${full_name}`} label={full_name} onDelete={() => onRemoveProduct(symbol)} size="small" sx={{ fontSize: 10 }} />
          ))}
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexDirection="column">
          <Typography variant="caption" fontSize={10}>
            Selected: {selectedUserProducts.length}/{maxProductsLimit}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => onChangeValues([])} style={{ fontSize: 9 }}>
            Clear All
          </Button>
        </Box>
      </Box>

      <Divider>
        <Typography variant="caption" fontSize={9}>
          Add / Remove Products Below
        </Typography>
      </Divider>

      <SearchGroupedProducts
        allProductGroups={allProductGroups}
        allSelectedProducts={allSelectedProducts}
        selectedUserProducts={selectedUserProducts}
        maxProductsLimit={maxProductsLimit}
        onProductClick={onCheckboxChange}
        getProductCount={getProductCount}
        isSelectionDisabled={isSelectionDisabled}
      />
    </Stack>
  ) : null;
};
